import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck, Radio } from "lucide-react";
import { relTime } from "@/lib/format";
import { useNavigate } from "react-router-dom";

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const load = () => api.get("/notifications").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    // Listen for live pushes fired by AppLayout's WebSocket
    const onLive = (e) => {
      const notif = e.detail;
      if (!notif || !notif.id) return;
      setItems((prev) => (prev.some((n) => n.id === notif.id) ? prev : [notif, ...prev]));
    };
    window.addEventListener("caos:notification", onLive);
    return () => window.removeEventListener("caos:notification", onLive);
  }, []);

  const markRead = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    if (n.link) navigate(n.link);
    load();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <div className="space-y-4" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Radio size={12} className="text-brand-emerald animate-pulse" />
          Live · assignments, comments and reminders show up here in real-time.
        </p>
        <Button variant="outline" size="sm" onClick={markAll} data-testid="mark-all-read">
          <CheckCheck size={13} className="mr-1.5" /> Mark all read
        </Button>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        {items.length === 0 && (
          <div className="p-12 text-center text-sm text-muted-foreground">
            <Bell size={20} className="mx-auto text-muted-foreground/40 mb-3" />
            You&rsquo;re all caught up.
          </div>
        )}
        {items.map((n) => (
          <button
            key={n.id}
            onClick={() => markRead(n)}
            data-testid={`notif-${n.id}`}
            className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted/40 flex items-start gap-3 ${!n.read ? "bg-brand-amber/5" : ""}`}
          >
            <div className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${!n.read ? "bg-brand-amber" : "bg-transparent"}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{n.title}</div>
              {n.body && <div className="text-xs text-muted-foreground mt-0.5 truncate">{n.body}</div>}
            </div>
            <div className="text-xs text-muted-foreground font-mono shrink-0">{relTime(n.created_at)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
